import { useParams, useSearchParams } from "react-router";
import { Download } from "lucide-react";

/**
 * Public P2P receipt (chek) page.
 *
 * Opened from a shared link: https://<frontend>/receipt/<id>?token=...
 * Works WITHOUT Telegram / app auth — the HMAC token in the query is the only
 * credential, and the PDF is fetched straight from the token-gated backend
 * endpoint (/wallet/PublicReceipt): inline for the preview iframe, attachment
 * for the download button. Rendered standalone from main.tsx (before initSDK),
 * so a recipient opening the link in any browser sees the chek — no Telegram
 * required. Copy is inlined (uz/ru/en) so the page needs no i18n bootstrap.
 */

const STRINGS = {
  uz: {
    title: "Chek",
    download: "Yuklab olish",
    invalid: "Havola yaroqsiz.",
  },
  ru: {
    title: "Чек",
    download: "Скачать",
    invalid: "Ссылка недействительна.",
  },
  en: {
    title: "Receipt",
    download: "Download",
    invalid: "The link is invalid.",
  },
} as const;

function pickLang(): keyof typeof STRINGS {
  const stored = (
    localStorage.getItem("locale") ||
    navigator.language ||
    "uz"
  )
    .slice(0, 2)
    .toLowerCase();
  if (stored === "ru") return "ru";
  if (stored === "en") return "en";
  return "uz";
}

export default function ReceiptPublicPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const tr = STRINGS[pickLang()];

  const token = params.get("token");
  const expires = params.get("expires");

  const apiBase = import.meta.env.VITE_API_BASE_URL;
  const valid = Boolean(id && token);
  const baseUrl = valid
    ? `${apiBase}wallet/PublicReceipt/${id}?token=${encodeURIComponent(
        token as string,
      )}${expires ? `&expires=${encodeURIComponent(expires)}` : ""}`
    : "";
  const previewUrl = valid ? `${baseUrl}&inline=1` : "";

  if (!valid) {
    return (
      <div className="flex min-h-[100dvh] flex-col items-center justify-center bg-dark px-6 text-center">
        <p className="text-sm leading-130 text-gray-1">{tr.invalid}</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-[100dvh] flex-col bg-dark">
      <header className="flex items-center justify-center px-4 py-4">
        <h1 className="text-base font-semibold leading-130 text-white">
          {tr.title}
        </h1>
      </header>

      <div className="flex-1 px-3">
        <iframe
          src={previewUrl}
          title={tr.title}
          className="h-full min-h-[62vh] w-full rounded-xl border border-white/08 bg-white"
        />
      </div>

      <div className="p-4 pb-safe-bottom">
        <a
          href={baseUrl}
          className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-yellow-linear text-sm font-semibold text-[#101017] transition-opacity active:opacity-90"
        >
          <Download className="h-[18px] w-[18px]" strokeWidth={2.2} />
          {tr.download}
        </a>
      </div>
    </div>
  );
}
